import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SecurityIcon from '@mui/icons-material/Security';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import RouterIcon from '@mui/icons-material/Router';
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined';
import {
  firewallService,
  FirewallGeraet,
  FirewallGeraetLoeschung,
  FirewallOverview,
  FirewallRegel,
  FirewallUpload,
  FirewallUploadTyp,
  FirewallUploadVorschlag,
  FirewallVdom,
} from '../../services/firewallService';
import {
  FirewallFileDetection,
  TYP_LABELS,
  UPLOAD_TYPEN,
  detectFirewallFile,
  formatLabel,
} from '../../utils/firewallFormat';
import {
  DEFAULT_RULE_FILTERS,
  FARBE_FILL,
  FARBE_LABEL,
  FirewallChoice,
  FirewallFarbe,
  NEW_FIREWALL_OPTION,
  RuleFilters,
  addressView,
  choiceFromVorschlag,
  colorBarCounts,
  filterRules,
  newFirewallNames,
  nextFirewallName,
  ratingByMember,
  resolveChoice,
  reviewLink,
  ruleDisagrees,
  sortGeraete,
  vdomParam,
} from '../../utils/firewallReview';
import { extractApiError } from '../../utils/errorUtils';
import { accordionSummarySx, accordionSx } from './auditDetailShared';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import FirewallRuleCard, { IpMismatchNote, RatingDot, RiskMarkers, ServiceChip, actionColor } from '../../components/firewall/FirewallRuleCard';

interface PendingFile {
  key: string;
  file: File;
  detection: FirewallFileDetection;
  vdom: string;
  needsVdom: boolean;
  /** Antwort von `uploads/vorschlag`; `null`, solange sie lädt oder fehlschlug. */
  vorschlag: FirewallUploadVorschlag | null;
  vorschlagLaedt: boolean;
  choice: FirewallChoice;
  /** Nutzer hat die Firewall selbst gewählt; dann folgt die Datei nicht mehr der Policy-Datei des Stapels. */
  manuell: boolean;
  error?: string | null;
}

interface MoveTarget {
  geraet: FirewallGeraet;
  block: FirewallVdom;
}

interface AuditFirewallSectionProps {
  auditId: number;
  isDark: boolean;
  expanded: boolean;
  canReview: boolean;
  onToggle: (event: React.SyntheticEvent, isExpanded: boolean) => void;
}

const TYPEN: FirewallUploadTyp[] = UPLOAD_TYPEN;
const ABSCHNITTE = ['0 byte rules', 'Rules without a comment', 'Other rules'];
const OFFEN_GREY_DARK = 'rgba(255,255,255,0.16)';
const OFFEN_GREY_LIGHT = 'rgba(0,0,0,0.12)';
const NEW_IN_MOVE = -1;

/**
 * Eine Spaltendefinition für jede Regelzeile, unabhängig vom Text.
 * Feste Spuren plus `minmax(0, …fr)`, damit Namen und Chips die Nachbarspalten nicht verschieben.
 * Die letzte Spur hängt nur an der Teamgröße, nicht am Zeileninhalt.
 * Schmal (unter 1200px) gilt stattdessen eine Spalte, die Zellen stapeln sich.
 */
function firewallRuleTracks(teamSize: number): string {
  const markers = 32 * teamSize + 64;
  return `168px minmax(0, 1.25fr) 140px minmax(0, 1.25fr) minmax(0, 1fr) 96px ${markers}px`;
}

function uniqueVdomUploads(uploads: FirewallUpload[]): FirewallUpload[] {
  const latest = new Map<string, FirewallUpload>();
  uploads.forEach((upload) => {
    const key = `${upload.typ}\0${upload.dateiname}`;
    const prev = latest.get(key);
    if (!prev || upload.id > prev.id) latest.set(key, upload);
  });
  return Array.from(latest.values()).sort((a, b) => uploadTitle(a).localeCompare(uploadTitle(b), 'de'));
}

function uploadsForVdom(overview: FirewallOverview | null, geraet: FirewallGeraet, block: FirewallVdom): FirewallUpload[] {
  return uniqueVdomUploads(
    (overview?.uploads ?? []).filter(
      (upload) => upload.firewall_id === geraet.id && (upload.vdom ?? '') === (block.vdom ?? '') && upload.format_id !== 'unknown',
    ),
  );
}

function uploadTitle(upload: FirewallUpload): string {
  const typ = TYP_LABELS[upload.typ] ?? upload.typ;
  return `${upload.dateiname} · ${typ}${upload.format_id === 'unknown' ? ' · unbekanntes Format' : ''}`;
}

function blockKey(block: Pick<FirewallVdom, 'firewall_id' | 'vdom'>): string {
  return `${block.firewall_id}:${vdomParam(block.vdom)}`;
}

function plural(count: number, singular: string, pluralForm: string): string {
  return `${count} ${count === 1 ? singular : pluralForm}`;
}

/**
 * Stapel-Regel: Adress- und Interface-Dateien folgen der Policy-Datei desselben VDOMs,
 * wenn diese in eine neue Firewall geht und der Nutzer sie nicht selbst zugeordnet hat.
 * Der Backend-Vorschlag kennt den Stapel nicht und würde sonst auf die bestehende Firewall zeigen.
 */
function followsPolicyFile(item: PendingFile): boolean {
  if (item.manuell || !item.vorschlag || item.detection.typ === 'policies' || item.choice.kind !== 'neu') return false;
  const own = item.vorschlag.vorschlag;
  return !(own.neu && (own.name ?? '').trim() === item.choice.name);
}

function harmonizePending(items: PendingFile[]): PendingFile[] {
  const policyChoice = new Map<string, FirewallChoice>();
  items.forEach((item) => {
    if (item.detection.typ === 'policies' && item.choice.kind === 'neu' && item.choice.name.trim()) {
      policyChoice.set((item.detection.vdom ?? item.vdom).trim().toLowerCase(), item.choice);
    }
  });
  if (policyChoice.size === 0) return items;
  return items.map((item) => {
    if (item.detection.typ === 'policies' || item.manuell || item.vorschlagLaedt || item.detection.formatId === 'unknown') return item;
    const follow = policyChoice.get((item.detection.vdom ?? item.vdom).trim().toLowerCase());
    if (!follow || follow.kind !== 'neu' || (item.choice.kind === 'neu' && item.choice.name === follow.name)) return item;
    return { ...item, choice: follow, error: null };
  });
}

const ColorBar: React.FC<{ rules: FirewallRegel[]; isDark: boolean }> = ({ rules, isDark }) => {
  const counts = colorBarCounts(rules);
  const total = rules.length || 1;
  const parts: { key: string; value: number; color: string; label: string }[] = [
    { key: 'gruen', value: counts.gruen, color: FARBE_FILL.gruen, label: 'Grün' },
    { key: 'gelb', value: counts.gelb, color: FARBE_FILL.gelb, label: 'Gelb' },
    { key: 'rot', value: counts.rot, color: FARBE_FILL.rot, label: 'Rot' },
    { key: 'offen', value: counts.offen, color: isDark ? OFFEN_GREY_DARK : OFFEN_GREY_LIGHT, label: 'Offen' },
  ];
  return (
    <Tooltip title={parts.map((part) => `${part.label}: ${part.value}`).join(' · ')} arrow>
      <Box data-testid="firewall-color-bar" sx={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', width: '100%', minWidth: 120 }}>
        {parts.filter((part) => part.value > 0).map((part) => (
          <Box key={part.key} sx={{ width: `${(part.value / total) * 100}%`, bgcolor: part.color }} />
        ))}
      </Box>
    </Tooltip>
  );
};

const VDOM_DELETE_FILES_LABEL = 'Dateien zum Löschen auswählen';
/** Feste Spaltenbreite pro Reviewer in VDOM-Zeilen (Ausrichtung über alle VDOMs). */
const VDOM_REVIEWER_COLUMN_WIDTH = 196;
const VDOM_COLOR_BAR_WIDTH = 168;
/** Horizontaler Abstand zwischen Reviewer-Block, Farbbalken und Menü (gleichmäßig). */
const VDOM_HEADER_COLUMN_GAP_PX = 14;
/** Höhe des Reviewer-Avatars (RatingDot); Farbbalken vertikal daran ausrichten. */
const VDOM_REVIEWER_AVATAR_SIZE = 24;
function firewallVdomGridColumns(teamCount: number): string {
  const reviewers = teamCount > 0 ? `repeat(${teamCount}, ${VDOM_REVIEWER_COLUMN_WIDTH}px) ` : '';
  return `minmax(0, 1fr) ${reviewers}${VDOM_COLOR_BAR_WIDTH}px minmax(${VDOM_HEADER_COLUMN_GAP_PX}px, auto)`;
}

const FinalCheckProgress: React.FC<{ done: number; total: number }> = ({ done, total }) => (
  <Box sx={{ minWidth: 0 }} data-testid="firewall-geraet-final-progress">
    <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.1, whiteSpace: 'nowrap' }}>
      Finaler Check · {done} von {total}
    </Typography>
    <LinearProgress
      variant="determinate"
      value={total ? (done / total) * 100 : 0}
      sx={{ height: 4, borderRadius: 2, mt: 0.25 }}
    />
  </Box>
);

const TeamMemberProgress: React.FC<{
  member: FirewallOverview['team'][number];
  done: number;
  total: number;
  showBar?: boolean;
}> = ({ member, done, total, showBar = true }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
    <RatingDot farbe={done === total && total > 0 ? 'gruen' : null} label={member.initialen} size={24} tooltip={member.name} />
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.1, whiteSpace: 'nowrap' }}>
        {member.name} · {done} von {total}
      </Typography>
      {showBar && (
        <LinearProgress
          variant="determinate"
          value={total ? (done / total) * 100 : 0}
          sx={{ height: 4, borderRadius: 2 }}
        />
      )}
    </Box>
  </Box>
);

const TeamProgress: React.FC<{
  team: FirewallOverview['team'];
  rules: FirewallRegel[];
  testId: string;
  layout?: 'grid' | 'inline' | 'vdom-right';
  showBar?: boolean;
}> = ({ team, rules, testId, layout = 'grid', showBar = true }) => {
  if (team.length === 0 || rules.length === 0) return null;
  const members = team.map((member) => {
    const done = rules.filter((rule) => ratingByMember(rule, member.id) !== null).length;
    const memberShowBar = layout === 'vdom-right' || (layout === 'grid' && showBar);
    return (
      <TeamMemberProgress
        key={member.id}
        member={member}
        done={done}
        total={rules.length}
        showBar={memberShowBar}
      />
    );
  });
  if (layout === 'vdom-right') {
    return (
      <Box
        data-testid={testId}
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(${team.length}, ${VDOM_REVIEWER_COLUMN_WIDTH}px)`,
          columnGap: 1.5,
          width: team.length * VDOM_REVIEWER_COLUMN_WIDTH + (team.length - 1) * 12,
          maxWidth: '100%',
          alignItems: 'start',
          justifyItems: 'stretch',
        }}
      >
        {members}
      </Box>
    );
  }
  if (layout === 'inline') {
    return (
      <Box
        sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', minWidth: 0, flex: 1 }}
        data-testid={testId}
      >
        {members}
      </Box>
    );
  }
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
        gap: 1.5,
        width: '100%',
      }}
      data-testid={testId}
    >
      {members}
    </Box>
  );
};

const BytesBar: React.FC<{ rule: FirewallRegel; isDark: boolean }> = ({ rule, isDark }) => {
  const bucket = rule.analyse.bucket ?? null;
  const width = bucket == null ? 0 : Math.max(4, (bucket / 13) * 100);
  return (
    <Tooltip title={rule.analyse.nie_genutzt ? 'nie genutzt' : `${rule.bytes}${rule.analyse.perzentil_audit != null ? ` · Top ${rule.analyse.perzentil_audit} % im Audit` : ''}`} arrow>
      <Box sx={{ width: '100%', minWidth: 0 }}>
        <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.1, fontFamily: 'ui-monospace, monospace', color: rule.analyse.nie_genutzt ? '#b26a00' : 'text.secondary', fontSize: '0.68rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'right' }}>
          {rule.analyse.nie_genutzt ? 'nie genutzt' : rule.bytes || '–'}
        </Typography>
        <Box sx={{ height: 5, borderRadius: 3, bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)', overflow: 'hidden' }}>
          <Box sx={{ width: `${width}%`, height: '100%', bgcolor: rule.analyse.nie_genutzt ? '#b26a00' : actionColor(rule.action) }} />
        </Box>
      </Box>
    </Tooltip>
  );
};

const AddressSummary: React.FC<{ items: FirewallRegel['source_aufgeloest']; align: 'left' | 'right' }> = ({ items, align }) => {
  const first = items[0];
  const more = items.length - 1;
  if (!first) return <Typography variant="body2" color="text.secondary">–</Typography>;
  const view = addressView(first);
  return (
    <Box sx={{ minWidth: 0, width: '100%', maxWidth: '100%', textAlign: align, overflow: 'hidden' }}>
      <Typography
        variant="body2"
        data-testid="firewall-row-address"
        title={view.headline}
        sx={{
          fontWeight: 700,
          fontFamily: view.monospace ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : undefined,
          color: first.ist_all && view.keineIp ? '#ef5350' : undefined,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {view.headline}
        {more > 0 && <Typography component="span" variant="caption" color="text.secondary"> +{more}</Typography>}
      </Typography>
      {view.secondary && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {view.secondary}
        </Typography>
      )}
      {view.keineIp && (
        <Typography
          variant="caption"
          data-testid="firewall-no-ip"
          sx={{ display: 'block', color: '#7a5200', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          Keine IP bekannt{view.hinweis ? ` · ${view.hinweis}` : ''}
        </Typography>
      )}
      {view.widerspruch && (
        <IpMismatchNote aufgeloest={view.widerspruch.aufgeloest} imNamen={view.widerspruch.im_namen} />
      )}
    </Box>
  );
};

const AuditFirewallSection: React.FC<AuditFirewallSectionProps> = ({
  auditId,
  isDark,
  expanded,
  canReview,
  onToggle,
}) => {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [overview, setOverview] = useState<FirewallOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadHinweis, setUploadHinweis] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingFile[]>([]);
  const [openVdoms, setOpenVdoms] = useState<Record<string, boolean>>({});
  const [openGeraete, setOpenGeraete] = useState<Record<number, boolean>>({});
  const [filters, setFilters] = useState<RuleFilters>(DEFAULT_RULE_FILTERS);
  const [drawerRuleId, setDrawerRuleId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<FirewallUpload | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Firewall-Verwaltung
  const [geraetMenu, setGeraetMenu] = useState<{ anchor: HTMLElement; geraet: FirewallGeraet } | null>(null);
  const [renaming, setRenaming] = useState<{ id: number; name: string } | null>(null);
  const [renameBusy, setRenameBusy] = useState(false);
  const [deleteGeraet, setDeleteGeraet] = useState<{ geraet: FirewallGeraet; counts: FirewallGeraetLoeschung | null } | null>(null);
  const [vdomMenu, setVdomMenu] = useState<{ anchor: HTMLElement; geraet: FirewallGeraet; block: FirewallVdom } | null>(null);
  const [uploadPickTarget, setUploadPickTarget] = useState<{ geraet: FirewallGeraet; block: FirewallVdom } | null>(null);
  const [moveTarget, setMoveTarget] = useState<MoveTarget | null>(null);
  const [moveZiel, setMoveZiel] = useState<number | ''>('');
  const [moveNeuName, setMoveNeuName] = useState('');
  const [moveBusy, setMoveBusy] = useState(false);
  const [moveError, setMoveError] = useState<string | null>(null);

  const geraete = useMemo(() => (overview ? sortGeraete(overview.geraete) : []), [overview]);
  const geraeteNames = useMemo(() => geraete.map((geraet) => geraet.name), [geraete]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await firewallService.getOverview(auditId, 'uebersicht');
      setOverview(data);
      const sorted = sortGeraete(data.geraete);
      const blockCount = sorted.reduce((sum, geraet) => sum + geraet.vdoms.length, 0);
      setOpenVdoms((current) => {
        const next = { ...current };
        sorted.forEach((geraet) => geraet.vdoms.forEach((block) => {
          const key = blockKey(block);
          if (next[key] === undefined) next[key] = blockCount <= 2;
        }));
        return next;
      });
      setOpenGeraete((current) => {
        const next = { ...current };
        sorted.forEach((geraet) => { if (next[geraet.id] === undefined) next[geraet.id] = true; });
        return next;
      });
    } catch (err) {
      setError(extractApiError(err));
    } finally {
      setLoading(false);
    }
  }, [auditId]);

  useEffect(() => {
    void load();
  }, [load]);

  const knownVdoms = useMemo(() => {
    const set = new Set<string>();
    geraete.forEach((geraet) => geraet.vdoms.forEach((block) => { if (block.vdom) set.add(block.vdom); }));
    overview?.uploads.forEach((upload) => { if (upload.vdom) set.add(upload.vdom); });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [geraete, overview]);

  // ── Upload-Dialog ───────────────────────────────────────────────────────

  const patchPending = useCallback((key: string, patch: Partial<PendingFile> | ((item: PendingFile) => Partial<PendingFile>)) => {
    setPending((current) => harmonizePending(current.map((entry) => (
      entry.key === key ? { ...entry, ...(typeof patch === 'function' ? patch(entry) : patch) } : entry
    ))));
  }, []);

  /** Holt den Firewall-Vorschlag für eine Datei; bei Fehlern bleibt die Auswahl offen. */
  const fetchVorschlag = useCallback(async (item: Pick<PendingFile, 'key' | 'file' | 'detection' | 'vdom'>) => {
    patchPending(item.key, { vorschlagLaedt: true });
    try {
      const vorschlag = await firewallService.vorschlag(auditId, item.file, item.detection.vdom ? null : item.vdom);
      patchPending(item.key, (entry) => ({
        vorschlag,
        vorschlagLaedt: false,
        choice: choiceFromVorschlag(vorschlag.vorschlag, nextFirewallName(vorschlag.geraete.map((geraet) => geraet.name))),
        error: entry.error && entry.error.startsWith('Bitte Firewall') ? null : entry.error,
      }));
    } catch (err) {
      patchPending(item.key, (entry) => ({
        vorschlag: null,
        vorschlagLaedt: false,
        choice: entry.choice.kind === 'offen' && geraete.length === 1 ? { kind: 'bestehend', id: geraete[0].id } : entry.choice,
        error: entry.detection.formatId === 'unknown' ? entry.error : extractApiError(err),
      }));
    }
  }, [auditId, geraete, patchPending]);

  const onPick = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = '';
    if (files.length === 0) return;
    const detected: PendingFile[] = await Promise.all(files.map(async (file) => {
      const detection = await detectFirewallFile(file);
      const needsVdom = detection.vdom == null && detection.formatId !== 'unknown' && detection.typ !== 'policies';
      return {
        key: `${file.name}-${file.size}-${file.lastModified}`,
        file,
        detection,
        vdom: detection.vdom ?? '',
        needsVdom,
        vorschlag: null,
        vorschlagLaedt: detection.formatId !== 'unknown',
        choice: { kind: 'offen' } as FirewallChoice,
        manuell: false,
      };
    }));
    setPending((current) => [...current, ...detected]);
    detected
      .filter((item) => item.detection.formatId !== 'unknown')
      .forEach((item) => { void fetchVorschlag(item); });
  };

  const setChoice = (item: PendingFile, value: string) => {
    if (value === NEW_FIREWALL_OPTION) {
      const names = Array.from(new Set([
        ...geraeteNames,
        ...(item.vorschlag?.geraete.map((geraet) => geraet.name) ?? []),
      ]));
      const suggested = item.vorschlag?.vorschlag.neu ? (item.vorschlag.vorschlag.name ?? '').trim() : '';
      patchPending(item.key, { choice: { kind: 'neu', name: suggested || nextFirewallName(names) }, manuell: true, error: null });
      return;
    }
    patchPending(item.key, { choice: { kind: 'bestehend', id: Number(value) }, manuell: true, error: null });
  };

  const uploadPending = async () => {
    setUploading(true);
    setError(null);
    setUploadHinweis(null);
    const remaining: PendingFile[] = [];
    const hinweise: string[] = [];
    const items = pending.map((item) => ({ ...item }));

    // Neue Firewalls einmal anlegen; derselbe Name in mehreren Dateien → dieselbe Firewall.
    const created = new Map<string, number>();
    for (const name of newFirewallNames(items.map((item) => item.choice))) {
      try {
        const geraet = await firewallService.createGeraet(auditId, name);
        created.set(name.toLowerCase(), geraet.id);
      } catch (err: unknown) {
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 409) {
          // Name existiert bereits (z. B. zwischenzeitlich angelegt): die bestehende Firewall verwenden.
          const existing = (await firewallService.listGeraete(auditId)).find((geraet) => geraet.name.toLowerCase() === name.toLowerCase());
          if (existing) created.set(name.toLowerCase(), existing.id);
        } else {
          setError(extractApiError(err));
        }
      }
    }

    for (const item of items) {
      const choice = resolveChoice(item.choice, created);
      if (item.needsVdom && !item.vdom.trim()) {
        remaining.push({ ...item, error: 'Bitte VDOM wählen.' });
        continue;
      }
      if (choice.kind !== 'bestehend' && item.detection.formatId !== 'unknown') {
        remaining.push({ ...item, choice, error: 'Bitte Firewall wählen.' });
        continue;
      }
      try {
        const uploaded = await firewallService.upload(auditId, item.file, {
          vdom: item.detection.vdom ? null : item.vdom,
          firewallId: choice.kind === 'bestehend' ? choice.id : null,
        });
        if (uploaded.nachtrag) {
          const missing = uploaded.nicht_zugeordnet ?? [];
          const base = `${item.file.name}: Fehlende Felder für ${uploaded.zugeordnet ?? 0} Regeln ergänzt`;
          hinweise.push(missing.length
            ? `${base}, ${missing.length} nicht zugeordnet (${missing.slice(0, 6).join(', ')})`
            : base);
        }
      } catch (err: unknown) {
        const detail = (err as { response?: { data?: { detail?: { code?: string; message?: string } & Partial<FirewallUploadVorschlag> } } })?.response?.data?.detail;
        if (detail && typeof detail === 'object' && detail.code === 'vdom_erforderlich') {
          remaining.push({ ...item, choice, needsVdom: true, error: detail.message ?? 'Bitte VDOM wählen.' });
        } else if (detail && typeof detail === 'object' && detail.code === 'firewall_erforderlich') {
          const vorschlag = detail.vorschlag ? (detail as FirewallUploadVorschlag) : null;
          remaining.push({
            ...item,
            vorschlag,
            choice: vorschlag ? choiceFromVorschlag(vorschlag.vorschlag, nextFirewallName(vorschlag.geraete.map((geraet) => geraet.name))) : choice,
            error: detail.message ?? 'Bitte Firewall wählen.',
          });
        } else {
          remaining.push({ ...item, choice, error: extractApiError(err) });
        }
      }
    }
    setPending(remaining);
    setUploadHinweis(hinweise.length ? hinweise.join(' · ') : null);
    setUploading(false);
    await load();
  };

  const reviewTarget = (mode: 'swipe' | 'final') => {
    const params = new URLSearchParams();
    if (filters.farbe !== 'alle') params.set('farbe', filters.farbe);
    const query = params.toString();
    return `/audits/${auditId}/firewall/${mode === 'swipe' ? 'bewertung' : 'final'}${query ? `?${query}` : ''}`;
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await firewallService.deleteUpload(auditId, deleteTarget.id);
      setDeleteTarget(null);
      await load();
    } catch (err) {
      setError(extractApiError(err));
    } finally {
      setDeleting(false);
    }
  };

  // ── Firewall-Verwaltung ─────────────────────────────────────────────────

  const submitRename = async () => {
    if (!renaming) return;
    const name = renaming.name.trim();
    const current = geraete.find((geraet) => geraet.id === renaming.id);
    if (!name || !current || name === current.name) {
      setRenaming(null);
      return;
    }
    setRenameBusy(true);
    try {
      await firewallService.updateGeraet(auditId, renaming.id, { name });
      setRenaming(null);
      await load();
    } catch (err) {
      setError(extractApiError(err));
    } finally {
      setRenameBusy(false);
    }
  };

  const openDeleteGeraet = async (geraet: FirewallGeraet) => {
    setDeleteGeraet({ geraet, counts: null });
    try {
      const counts = await firewallService.geraetLoeschvorschau(auditId, geraet.id);
      setDeleteGeraet((current) => (current && current.geraet.id === geraet.id ? { geraet, counts } : current));
    } catch (err) {
      setError(extractApiError(err));
    }
  };

  const confirmDeleteGeraet = async () => {
    if (!deleteGeraet) return;
    setDeleting(true);
    try {
      await firewallService.deleteGeraet(auditId, deleteGeraet.geraet.id);
      setDeleteGeraet(null);
      await load();
    } catch (err) {
      setError(extractApiError(err));
    } finally {
      setDeleting(false);
    }
  };

  const openMove = (geraet: FirewallGeraet, block: FirewallVdom) => {
    const others = geraete.filter((entry) => entry.id !== geraet.id);
    setMoveTarget({ geraet, block });
    setMoveZiel(others.length === 1 ? others[0].id : others.length === 0 ? NEW_IN_MOVE : '');
    setMoveNeuName(nextFirewallName(geraeteNames));
    setMoveError(null);
  };

  const confirmMove = async () => {
    if (!moveTarget || moveZiel === '') return;
    setMoveBusy(true);
    setMoveError(null);
    try {
      let zielId = moveZiel;
      if (zielId === NEW_IN_MOVE) {
        const name = moveNeuName.trim();
        if (!name) {
          setMoveError('Bitte einen Namen für die neue Firewall angeben.');
          setMoveBusy(false);
          return;
        }
        zielId = (await firewallService.createGeraet(auditId, name)).id;
      }
      await firewallService.moveVdom(auditId, moveTarget.geraet.id, vdomParam(moveTarget.block.vdom), zielId);
      setMoveTarget(null);
      await load();
    } catch (err) {
      setMoveError(extractApiError(err));
    } finally {
      setMoveBusy(false);
    }
  };

  // ── Abgeleitete Daten ───────────────────────────────────────────────────

  const allRules = useMemo(() => geraete.flatMap((geraet) => geraet.vdoms.flatMap((block) => block.regeln)), [geraete]);
  const vdomCount = useMemo(() => geraete.reduce((sum, geraet) => sum + geraet.vdoms.length, 0), [geraete]);
  const drawerRule = useMemo(() => allRules.find((rule) => rule.id === drawerRuleId) ?? null, [allRules, drawerRuleId]);
  const drawerContext = useMemo(() => {
    for (const geraet of geraete) {
      const block = geraet.vdoms.find((entry) => entry.regeln.some((rule) => rule.id === drawerRuleId));
      if (block) return { geraet, block };
    }
    return null;
  }, [geraete, drawerRuleId]);

  const filtersActive = filters.abschnitt !== 'alle' || filters.farbe !== 'alle' || filters.offenFuer !== 'alle'
    || filters.risiko || filters.any || filters.abweichung || filters.suche.trim().length > 0;

  const team = overview?.team ?? [];
  const otherUploads = (overview?.uploads ?? []).filter(
    (upload) => !upload.vdom || upload.format_id === 'unknown',
  );

  const deleteMessage = deleteTarget
    ? deleteTarget.typ === 'policies'
      ? `${deleteTarget.regeln_aktuell} Regel${deleteTarget.regeln_aktuell === 1 ? '' : 'n'}, ${deleteTarget.bewertungen_anzahl} Bewertung${deleteTarget.bewertungen_anzahl === 1 ? '' : 'en'} und ${deleteTarget.konsens_anzahl} Konsens-Eintr${deleteTarget.konsens_anzahl === 1 ? 'ag' : 'äge'} werden endgültig gelöscht.`
      : `${deleteTarget.objekt_anzahl} Objekte verschwinden. Die Regeln bleiben, die Karten zeigen wieder die rohen Objektnamen.`
    : '';

  const geraetDeleteMessage = deleteGeraet?.counts
    ? `${plural(deleteGeraet.counts.uploads, 'Upload', 'Uploads')}, ${plural(deleteGeraet.counts.regeln, 'Regel', 'Regeln')}, ${plural(deleteGeraet.counts.bewertungen, 'Bewertung', 'Bewertungen')}, ${plural(deleteGeraet.counts.konsens, 'Konsens-Eintrag', 'Konsens-Einträge')} und ${plural(deleteGeraet.counts.objekte, 'Objekt', 'Objekte')} werden endgültig gelöscht.`
    : 'Zähle Inhalte …';

  // ── Renderer ────────────────────────────────────────────────────────────

  const renderUploadChip = (upload: FirewallUpload) => (
    <Chip
      key={upload.id}
      data-testid="firewall-upload-status"
      size="small"
      variant="outlined"
      color={upload.format_id === 'unknown' ? 'warning' : 'default'}
      label={uploadTitle(upload)}
      onDelete={() => setDeleteTarget(upload)}
      deleteIcon={(
        <Tooltip title="Upload löschen" arrow>
          <DeleteOutlineIcon data-testid="firewall-upload-delete" />
        </Tooltip>
      )}
      sx={{ maxWidth: '100%' }}
    />
  );

  const renderRuleRow = (rule: FirewallRegel) => {
    const disagreement = ruleDisagrees(rule);
    const color = actionColor(rule.action);
    return (
      <Paper
        key={rule.id}
        variant="outlined"
        data-testid="firewall-rule-row"
        onClick={() => setDrawerRuleId(rule.id)}
        sx={{
          display: 'grid',
          gridTemplateColumns: 'var(--firewall-rule-columns, minmax(0, 1fr))',
          alignItems: 'center',
          columnGap: 1.25,
          rowGap: 0.75,
          width: '100%',
          minWidth: 0,
          boxSizing: 'border-box',
          px: 1.5,
          py: 1,
          borderRadius: 2,
          cursor: 'pointer',
          borderLeft: `4px solid ${color}`,
          opacity: rule.entfernt ? 0.55 : 1,
          borderColor: disagreement ? 'warning.main' : undefined,
          transition: 'border-color 0.15s, background-color 0.15s',
          '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.025)' },
        }}
      >
        <Box data-testid="firewall-col-rule" sx={{ minWidth: 0, overflow: 'hidden' }}>
          <Typography variant="body2" title={rule.anzeigename} sx={{ fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {rule.anzeigename}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'ui-monospace, monospace' }}>
              ID {rule.policy_id}
            </Typography>
            <RiskMarkers rule={rule} />
            {rule.inhalt_geaendert && <Chip size="small" color="warning" label="geändert" sx={{ height: 16, fontSize: '0.6rem' }} />}
            {rule.entfernt && <Chip size="small" label="entfernt" sx={{ height: 16, fontSize: '0.6rem' }} />}
            {rule.weicht_vom_final_ab && <Chip size="small" color="warning" label="≠ Final" sx={{ height: 16, fontSize: '0.6rem' }} />}
          </Box>
        </Box>
        <Box data-testid="firewall-col-source" sx={{ minWidth: 0, overflow: 'hidden' }}>
          <AddressSummary items={rule.source_aufgeloest} align="left" />
        </Box>
        <Box
          data-testid="firewall-col-action"
          sx={{
            minWidth: 0,
            overflow: 'hidden',
            display: 'grid',
            gridTemplateColumns: { xs: '28px auto 16px', lg: '28px minmax(0, 1fr) 16px' },
            alignItems: 'center',
            columnGap: 0.5,
            width: { xs: 'max-content', lg: '100%' },
            maxWidth: '100%',
            justifySelf: { xs: 'start', lg: 'stretch' },
          }}
        >
          <Box sx={{ width: '100%', height: 0, borderTop: `3px ${rule.action.toUpperCase() === 'ACCEPT' ? 'solid' : 'dashed'} ${color}` }} />
          <Chip
            size="small"
            label={rule.action || '–'}
            sx={{
              justifySelf: 'center',
              maxWidth: '100%',
              bgcolor: color,
              color: '#fff',
              fontWeight: 800,
              height: 20,
              fontSize: '0.65rem',
              '& .MuiChip-label': { px: 0.75, overflow: 'hidden', textOverflow: 'ellipsis' },
            }}
          />
          <ArrowForwardIcon sx={{ fontSize: 16, color, justifySelf: 'center' }} />
        </Box>
        <Box data-testid="firewall-col-dest" sx={{ minWidth: 0, overflow: 'hidden' }}>
          <AddressSummary items={rule.destination_aufgeloest} align="left" />
        </Box>
        <Box
          data-testid="firewall-col-ports"
          sx={{
            minWidth: 0,
            overflow: 'hidden',
            display: 'flex',
            gap: 0.5,
            flexWrap: 'wrap',
            alignItems: 'center',
            alignContent: 'center',
            '& > *': { maxWidth: '100%', flexShrink: 0 },
            '& .MuiChip-root': { maxWidth: '100%', height: 'auto' },
            '& .MuiChip-label': { whiteSpace: 'normal', lineHeight: 1.25, py: 0.25 },
          }}
        >
          {rule.services.slice(0, 3).map((service, index) => (
            <ServiceChip key={`${service.raw}-${index}`} service={service} compact showCount={false} />
          ))}
          {rule.services.length > 3 && (
            <Chip size="small" variant="outlined" label={`+${rule.services.length - 3}`} sx={{ height: 24 }} />
          )}
        </Box>
        <Box data-testid="firewall-col-bytes" sx={{ minWidth: 0 }}>
          <BytesBar rule={rule} isDark={isDark} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, flexWrap: 'nowrap' }} data-testid="firewall-rule-dots">
          {team.map((member) => {
            const farbe = ratingByMember(rule, member.id);
            return (
              <RatingDot
                key={member.id}
                farbe={farbe}
                label={member.initialen}
                size={26}
                tooltip={`${member.name}: ${farbe ? FARBE_LABEL[farbe] : 'offen'}`}
              />
            );
          })}
          <Box sx={{ width: 1, height: 22, bgcolor: 'divider', mx: 0.5 }} />
          <RatingDot
            farbe={rule.konsens?.farbe ?? null}
            label="F"
            size={26}
            tooltip={rule.konsens ? `Konsens: ${FARBE_LABEL[rule.konsens.farbe]}` : 'Konsens offen'}
          />
        </Box>
      </Paper>
    );
  };

  const renderVdomBlock = (geraet: FirewallGeraet, block: FirewallVdom) => {
    const key = blockKey(block);
    const open = openVdoms[key] ?? true;
    const active = block.regeln.filter((rule) => !rule.entfernt);
    const visible = filterRules(block.regeln, filters);
    const disagreements = active.filter(ruleDisagrees).length;
    const presentTypes = TYPEN.filter((typ) => Boolean(block.dateien?.[typ]));
    const missingTypes = TYPEN.filter((typ) => !block.dateien?.[typ]);
    const missingLabel = missingTypes.map((typ) => TYP_LABELS[typ]).join(', ');
    const showTeamProgress = team.length > 0 && active.length > 0;
    const reviewerStartCol = 2;
    const barCol = showTeamProgress ? reviewerStartCol + team.length : reviewerStartCol;
    const menuCol = barCol + 1;
    return (
      <Paper
        key={key}
        variant="outlined"
        data-testid="firewall-vdom-block"
        data-firewall-id={geraet.id}
        data-vdom={vdomParam(block.vdom)}
        sx={{ mb: 1, borderRadius: 2, overflow: 'hidden' }}
      >
        <Box
          onClick={() => setOpenVdoms((current) => ({ ...current, [key]: !open }))}
          sx={{
            px: 1.5,
            py: 1,
            cursor: 'pointer',
            background: isDark
              ? 'linear-gradient(135deg, rgba(30,36,68,0.5) 0%, rgba(22,28,52,0.3) 100%)'
              : 'linear-gradient(135deg, rgba(245,247,252,1) 0%, rgba(238,241,248,1) 100%)',
          }}
        >
          <Box
            sx={{
              display: 'grid',
              alignItems: 'start',
              columnGap: `${VDOM_HEADER_COLUMN_GAP_PX}px`,
              minWidth: 0,
              gridTemplateRows: 'auto',
              gridTemplateColumns: showTeamProgress
                ? firewallVdomGridColumns(team.length)
                : `minmax(0, 1fr) ${VDOM_COLOR_BAR_WIDTH}px minmax(${VDOM_HEADER_COLUMN_GAP_PX}px, auto)`,
            }}
          >
            <Box
              sx={{
                gridColumn: 1,
                gridRow: 1,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                minWidth: 0,
                alignSelf: 'start',
              }}
            >
              <IconButton
                size="small"
                sx={{ p: 0.25, mt: 0.125, flexShrink: 0 }}
                aria-label={open ? 'zuklappen' : 'aufklappen'}
              >
                {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0, minWidth: 0 }}>
                <Typography
                  variant="subtitle1"
                  component="div"
                  sx={{
                    fontWeight: 700,
                    lineHeight: 1.15,
                    whiteSpace: 'nowrap',
                    m: 0,
                    p: 0,
                    minWidth: 0,
                  }}
                  data-testid="firewall-vdom-title"
                >
                  {block.label}
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 500, ml: 1 }}>
                    {block.gesamt} Regel{block.gesamt === 1 ? '' : 'n'}
                  </Typography>
                </Typography>
                {(presentTypes.length > 0 || missingTypes.length > 0) && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 0.5,
                      mt: '6px',
                      m: 0,
                      p: 0,
                    }}
                    onClick={(event) => event.stopPropagation()}
                    data-testid="firewall-file-status"
                  >
                  {presentTypes.map((typ) => (
                    <Chip
                      key={typ}
                      size="small"
                      data-testid={`firewall-file-${typ}`}
                      data-present="ja"
                      icon={<CheckCircleIcon />}
                      label={TYP_LABELS[typ]}
                      color="success"
                      variant="filled"
                      sx={{ height: 22, fontSize: '0.72rem', '& .MuiChip-label': { px: 0.75, whiteSpace: 'nowrap' } }}
                    />
                  ))}
                  {missingTypes.length > 0 && (
                    <Tooltip title={`Noch nicht hochgeladen: ${missingLabel}`} arrow>
                      <InfoOutlinedIcon
                        data-testid="firewall-file-missing-info"
                        fontSize="small"
                        sx={{ color: 'text.secondary', cursor: 'help' }}
                      />
                    </Tooltip>
                  )}
                  </Box>
                )}
              </Box>
            </Box>
            {showTeamProgress && (
              <Box sx={{ gridColumn: `${reviewerStartCol} / ${reviewerStartCol + team.length}`, gridRow: 1, alignSelf: 'start' }}>
                <TeamProgress team={team} rules={active} testId="firewall-vdom-reviewer-progress" layout="vdom-right" />
              </Box>
            )}
            <Box
              data-testid="firewall-vdom-color-column"
              sx={{
                gridColumn: barCol,
                gridRow: 1,
                width: VDOM_COLOR_BAR_WIDTH,
                maxWidth: VDOM_COLOR_BAR_WIDTH,
                minWidth: VDOM_COLOR_BAR_WIDTH,
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                gap: '2px',
                alignSelf: 'start',
              }}
              onClick={(event) => event.stopPropagation()}
            >
              <Box
                sx={{
                  height: VDOM_REVIEWER_AVATAR_SIZE,
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <ColorBar rules={active} isDark={isDark} />
              </Box>
              {disagreements > 0 && (
                <Chip
                  size="small"
                  color="warning"
                  icon={<WarningAmberIcon sx={{ fontSize: '14px !important' }} />}
                  data-testid="firewall-disagreement-count"
                  label={`${disagreements} Abweichung${disagreements === 1 ? '' : 'en'}`}
                  sx={{
                    width: '100%',
                    maxWidth: VDOM_COLOR_BAR_WIDTH,
                    height: 'auto',
                    minHeight: 22,
                    fontSize: '0.62rem',
                    borderRadius: 1.5,
                    display: 'flex',
                    justifyContent: 'flex-start',
                    '& .MuiChip-label': {
                      px: 0.5,
                      py: 0.25,
                      whiteSpace: 'normal',
                      lineHeight: 1.15,
                      textAlign: 'left',
                    },
                  }}
                />
              )}
            </Box>
            <Box
              sx={{
                gridColumn: menuCol,
                gridRow: 1,
                alignSelf: 'start',
                height: VDOM_REVIEWER_AVATAR_SIZE,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: VDOM_HEADER_COLUMN_GAP_PX,
              }}
            >
              <Tooltip title="VDOM-Menü" arrow>
                <IconButton
                  size="small"
                  data-testid="firewall-vdom-menu"
                  aria-label={`Menü für ${block.label}`}
                  sx={{ p: 0.5 }}
                  onClick={(event) => { event.stopPropagation(); setVdomMenu({ anchor: event.currentTarget, geraet, block }); }}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>
        <Collapse in={open}>
          <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {block.regeln.length === 0 && (
              <Typography variant="body2" color="text.secondary">Noch keine Policies für diesen VDOM.</Typography>
            )}
            {block.regeln.length > 0 && visible.length === 0 && (
              <Typography variant="body2" color="text.secondary">Keine Regel passt zu den Filtern.</Typography>
            )}
            {visible.length > 0 && (
              <Box
                data-testid="firewall-rule-grid"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.75,
                  minWidth: 0,
                  '--firewall-rule-columns': 'minmax(0, 1fr)',
                  '@media (min-width:1200px)': {
                    '--firewall-rule-columns': firewallRuleTracks(team.length),
                  },
                }}
              >
                {visible.map((rule) => renderRuleRow(rule))}
              </Box>
            )}
          </Box>
        </Collapse>
      </Paper>
    );
  };

  const renderGeraetBlock = (geraet: FirewallGeraet) => {
    const open = openGeraete[geraet.id] ?? true;
    const active = geraet.vdoms.flatMap((block) => block.regeln.filter((rule) => !rule.entfernt));
    const isRenaming = renaming?.id === geraet.id;
    const finalized = active.filter((rule) => rule.konsens != null).length;
    const showTeamProgress = team.length > 0 && active.length > 0;
    const geraetReviewerStartCol = 2;
    const geraetBarCol = showTeamProgress ? geraetReviewerStartCol + team.length : geraetReviewerStartCol;
    const geraetMenuCol = geraetBarCol + 1;
    const showGeraetProgressRow = active.length > 0 || showTeamProgress;
    return (
      <Paper
        key={geraet.id}
        variant="outlined"
        data-testid="firewall-geraet-block"
        data-firewall-id={geraet.id}
        sx={{ mb: 2, borderRadius: 3, overflow: 'hidden', borderColor: isDark ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.18)' }}
      >
        <Box
          sx={{
            pl: { xs: 1, md: 3 },
            pr: 1,
            py: 1.5,
            bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
            borderBottom: open ? '1px solid' : 'none',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ px: 1.5, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'nowrap', minWidth: 0 }}>
              <IconButton
                size="small"
                sx={{ p: 0.25, flexShrink: 0 }}
                aria-label={open ? 'Firewall zuklappen' : 'Firewall aufklappen'}
                onClick={() => setOpenGeraete((current) => ({ ...current, [geraet.id]: !open }))}
              >
                {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
              <RouterIcon sx={{ color: 'primary.main', flexShrink: 0 }} />
              {isRenaming ? (
                <TextField
                  size="small"
                  autoFocus
                  value={renaming.name}
                  disabled={renameBusy}
                  inputProps={{ 'data-testid': 'firewall-geraet-rename-input', maxLength: 200 }}
                  onChange={(event) => setRenaming({ id: geraet.id, name: event.target.value })}
                  onBlur={() => { void submitRename(); }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') { event.preventDefault(); void submitRename(); }
                    if (event.key === 'Escape') setRenaming(null);
                  }}
                  sx={{ minWidth: 220, flexShrink: 0 }}
                />
              ) : (
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'text', flexShrink: 0 }}
                  onClick={() => setRenaming({ id: geraet.id, name: geraet.name })}
                  title="Zum Umbenennen klicken"
                >
                  <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2, whiteSpace: 'nowrap' }} data-testid="firewall-geraet-name">
                    {geraet.name}
                  </Typography>
                  <EditOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                </Box>
              )}
              <Typography
                variant="body2"
                color="text.secondary"
                data-testid="firewall-geraet-counts"
                sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                {plural(geraet.vdoms.length, 'VDOM', 'VDOMs')} · {plural(geraet.gesamt, 'Regel', 'Regeln')}
              </Typography>
              <Box
                sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', alignSelf: 'stretch' }}
                data-testid="firewall-geraet-overview-bar"
              >
                <ColorBar rules={active} isDark={isDark} />
              </Box>
            </Box>
            {showGeraetProgressRow && (
              <Box
                sx={{
                  display: 'grid',
                  alignItems: 'start',
                  columnGap: `${VDOM_HEADER_COLUMN_GAP_PX}px`,
                  minWidth: 0,
                  mt: 0.75,
                  gridTemplateColumns: showTeamProgress
                    ? firewallVdomGridColumns(team.length)
                    : `minmax(0, 1fr) ${VDOM_COLOR_BAR_WIDTH}px minmax(${VDOM_HEADER_COLUMN_GAP_PX}px, auto)`,
                }}
              >
                {active.length > 0 && (
                  <Box sx={{ gridColumn: 1, maxWidth: 320 }}>
                    <FinalCheckProgress done={finalized} total={active.length} />
                  </Box>
                )}
                {showTeamProgress && (
                  <Box sx={{ gridColumn: `${geraetReviewerStartCol} / ${geraetReviewerStartCol + team.length}` }}>
                    <TeamProgress team={team} rules={active} testId="firewall-geraet-team-progress" layout="vdom-right" />
                  </Box>
                )}
                {showTeamProgress && (
                  <Box
                    sx={{
                      gridColumn: geraetBarCol,
                      width: VDOM_COLOR_BAR_WIDTH,
                      maxWidth: VDOM_COLOR_BAR_WIDTH,
                      minWidth: VDOM_COLOR_BAR_WIDTH,
                      boxSizing: 'border-box',
                      height: VDOM_REVIEWER_AVATAR_SIZE,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <ColorBar rules={active} isDark={isDark} />
                  </Box>
                )}
                <Box
                  sx={{
                    gridColumn: showTeamProgress ? geraetMenuCol : 1,
                    gridRow: 1,
                    justifySelf: showTeamProgress ? undefined : 'end',
                    alignSelf: 'start',
                    height: VDOM_REVIEWER_AVATAR_SIZE,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: VDOM_HEADER_COLUMN_GAP_PX,
                  }}
                >
                  <Tooltip title="Firewall-Menü" arrow>
                    <IconButton
                      size="small"
                      data-testid="firewall-geraet-menu"
                      aria-label={`Menü für ${geraet.name}`}
                      sx={{ p: 0.5 }}
                      onClick={(event) => setGeraetMenu({ anchor: event.currentTarget, geraet })}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            )}
            {!showGeraetProgressRow && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                <Tooltip title="Firewall-Menü" arrow>
                  <IconButton
                    size="small"
                    data-testid="firewall-geraet-menu"
                    aria-label={`Menü für ${geraet.name}`}
                    sx={{ p: 0.5 }}
                    onClick={(event) => setGeraetMenu({ anchor: event.currentTarget, geraet })}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>
        </Box>
        <Collapse in={open}>
          <Box sx={{ pl: { xs: 1, md: 3 }, pr: 1, pt: 1.5, pb: 0.5 }}>
            {geraet.vdoms.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ pb: 1.5 }}>Noch keine Uploads für diese Firewall.</Typography>
            )}
            {geraet.vdoms.map((block) => renderVdomBlock(geraet, block))}
          </Box>
        </Collapse>
      </Paper>
    );
  };

  const renderPendingFile = (item: PendingFile) => {
    const options = item.vorschlag?.geraete ?? geraete.map((geraet) => ({ id: geraet.id, name: geraet.name }));
    const selectValue = item.choice.kind === 'bestehend' ? String(item.choice.id) : item.choice.kind === 'neu' ? NEW_FIREWALL_OPTION : '';
    const showFirewall = item.detection.formatId !== 'unknown';
    return (
      <Box
        key={item.key}
        data-testid="firewall-pending-file"
        sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, py: 1, borderBottom: '1px solid', borderColor: 'divider', '&:last-of-type': { borderBottom: 'none' } }}
      >
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 180 }}>{item.file.name}</Typography>
          <Chip
            size="small"
            color={item.detection.formatId === 'unknown' ? 'warning' : 'primary'}
            variant="outlined"
            label={item.detection.formatId === 'unknown' ? 'unbekanntes Format' : `${TYP_LABELS[item.detection.typ]} · ${formatLabel(item.detection.formatId)}`}
          />
          {item.detection.vdom && (
            <Chip size="small" label={`VDOM ${item.detection.vdom}`} data-testid="firewall-pending-vdom" />
          )}
          {!item.detection.vdom && item.detection.formatId !== 'unknown' && (
            <Autocomplete
              freeSolo
              size="small"
              options={knownVdoms}
              value={item.vdom}
              onInputChange={(_, value) => patchPending(item.key, { vdom: value, error: null })}
              onBlur={() => { void fetchVorschlag(item); }}
              sx={{ minWidth: 220 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={item.needsVdom ? 'VDOM wählen (erforderlich)' : 'VDOM wählen (optional)'}
                  error={Boolean(item.error) && item.error === 'Bitte VDOM wählen.'}
                  inputProps={{ ...params.inputProps, 'data-testid': 'firewall-vdom-select' }}
                />
              )}
            />
          )}
          {showFirewall && (
            <FormControl size="small" sx={{ minWidth: 220 }} error={Boolean(item.error?.startsWith('Bitte Firewall'))}>
              <InputLabel id={`fw-choice-${item.key}`}>Firewall</InputLabel>
              <Select
                labelId={`fw-choice-${item.key}`}
                label="Firewall"
                value={selectValue}
                displayEmpty
                disabled={uploading}
                inputProps={{ 'data-testid': 'firewall-choice-select' }}
                onChange={(event) => setChoice(item, String(event.target.value))}
                renderValue={(value) => {
                  if (value === NEW_FIREWALL_OPTION) return 'Neue Firewall …';
                  const found = options.find((option) => String(option.id) === value);
                  return found ? found.name : <Typography component="span" color="text.secondary">Bitte wählen</Typography>;
                }}
              >
                {options.map((option) => (
                  <MenuItem key={option.id} value={String(option.id)} data-testid="firewall-choice-option">{option.name}</MenuItem>
                ))}
                <MenuItem value={NEW_FIREWALL_OPTION} data-testid="firewall-choice-new">Neue Firewall …</MenuItem>
              </Select>
            </FormControl>
          )}
          {showFirewall && item.choice.kind === 'neu' && (
            <TextField
              size="small"
              label="Name der neuen Firewall"
              value={item.choice.name}
              disabled={uploading}
              inputProps={{ 'data-testid': 'firewall-new-name', maxLength: 200 }}
              onChange={(event) => patchPending(item.key, { choice: { kind: 'neu', name: event.target.value }, manuell: true, error: null })}
              sx={{ minWidth: 220 }}
            />
          )}
          {item.vorschlagLaedt && <CircularProgress size={18} />}
          <IconButton size="small" aria-label="entfernen" sx={{ ml: 'auto' }} onClick={() => setPending((current) => current.filter((entry) => entry.key !== item.key))}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        {showFirewall && item.vorschlag && !item.vorschlagLaedt && (
          <Typography variant="caption" color="text.secondary" data-testid="firewall-vorschlag-grund" sx={{ pl: 0.5 }}>
            Vorschlag: {followsPolicyFile(item) ? `Folgt der Policy-Datei für ${item.vorschlag.vdom_label} aus diesem Upload.` : item.vorschlag.vorschlag.grund}
          </Typography>
        )}
        {item.error && <Typography variant="caption" color="error" data-testid="firewall-pending-error" sx={{ pl: 0.5 }}>{item.error}</Typography>}
      </Box>
    );
  };

  const moveOthers = moveTarget ? geraete.filter((entry) => entry.id !== moveTarget.geraet.id) : [];
  const moveConflict = moveTarget && typeof moveZiel === 'number' && moveZiel !== NEW_IN_MOVE
    ? moveOthers.find((entry) => entry.id === moveZiel)?.vdoms.some((block) => vdomParam(block.vdom) === vdomParam(moveTarget.block.vdom)) ?? false
    : false;

  return (
    <Accordion expanded={expanded} onChange={onToggle} sx={accordionSx(isDark)}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx(isDark)}>
        <SecurityIcon sx={{ fontSize: 20, color: 'primary.main' }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Firewall
        </Typography>
        {overview && overview.statistik.regeln > 0 && (
          <Typography variant="body2" color="text.secondary" data-testid="firewall-summary-counts">
            {overview.statistik.regeln} Regeln · {plural(geraete.length, 'Firewall', 'Firewalls')} · {plural(vdomCount, 'VDOM', 'VDOMs')}
          </Typography>
        )}
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2, py: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
          <Button variant="outlined" startIcon={<UploadFileIcon />} onClick={() => inputRef.current?.click()}>
            Dateien wählen
          </Button>
          <input
            ref={inputRef}
            data-testid="firewall-file-input"
            type="file"
            multiple
            hidden
            onChange={(event) => { void onPick(event); }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto' }}>
            Pro Firewall und VDOM: Policies, Adressen, Adressgruppen, Dienste, Dienstgruppen, Virtual IPs, VIP-Gruppen, IP-Pools und Interfaces. Typ und Firewall werden am Inhalt erkannt und vorgeschlagen.
          </Typography>
          {canReview && (
            <>
              <Button variant="contained" data-testid="firewall-bewerten" onClick={() => navigate(reviewTarget('swipe'))}>
                Bewerten
              </Button>
              <Button variant="contained" color="secondary" onClick={() => navigate(reviewTarget('final'))}>
                Finaler Check
              </Button>
            </>
          )}
        </Box>
        {!canReview && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Bewertung und Finaler Check stehen Pentestern im Audit-Team offen.
          </Typography>
        )}

        {pending.length > 0 && (
          <Paper variant="outlined" sx={{ mb: 2, p: 1.5, borderRadius: 2 }} data-testid="firewall-format-hint">
            {pending.map(renderPendingFile)}
            <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
              <Button
                variant="contained"
                disabled={uploading || pending.some((item) => item.vorschlagLaedt)}
                data-testid="firewall-upload-submit"
                onClick={() => { void uploadPending(); }}
              >
                {uploading ? 'Wird importiert…' : `${pending.length === 1 ? 'Datei' : `${pending.length} Dateien`} hochladen`}
              </Button>
              <Button variant="text" disabled={uploading} onClick={() => setPending([])}>Abbrechen</Button>
            </Box>
          </Paper>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {uploadHinweis && (
          <Alert severity="info" sx={{ mb: 2 }} data-testid="firewall-nachtrag-hinweis" onClose={() => setUploadHinweis(null)}>
            {uploadHinweis}
          </Alert>
        )}
        {loading && <CircularProgress size={28} />}

        {!loading && overview && (
          <>
            {otherUploads.length > 0 && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">Weitere Uploads:</Typography>
                {otherUploads.map(renderUploadChip)}
              </Box>
            )}
            {geraete.length === 0 && (
              <Alert severity="info">Noch keine importierten Regeln.</Alert>
            )}

            {allRules.length > 0 && (
              <Paper
                variant="outlined"
                data-testid="firewall-filters"
                sx={{ p: 1.5, mb: 2, borderRadius: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}
              >
                <TextField
                  size="small"
                  placeholder="Objekt, IP oder Port"
                  value={filters.suche}
                  onChange={(event) => setFilters((current) => ({ ...current, suche: event.target.value }))}
                  inputProps={{ 'data-testid': 'firewall-search' }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                  sx={{ minWidth: 220 }}
                />
                <FormControl size="small" sx={{ minWidth: 190 }}>
                  <InputLabel>Abschnitt</InputLabel>
                  <Select
                    label="Abschnitt"
                    value={filters.abschnitt}
                    onChange={(event) => setFilters((current) => ({ ...current, abschnitt: event.target.value }))}
                  >
                    <MenuItem value="alle">Alle Abschnitte</MenuItem>
                    {ABSCHNITTE.map((abschnitt) => <MenuItem key={abschnitt} value={abschnitt}>{abschnitt}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Farbe</InputLabel>
                  <Select
                    label="Farbe"
                    data-testid="firewall-overview-color"
                    value={filters.farbe}
                    onChange={(event) => setFilters((current) => ({ ...current, farbe: event.target.value as RuleFilters['farbe'] }))}
                  >
                    <MenuItem value="alle">Alle Farben</MenuItem>
                    {(['gruen', 'gelb', 'rot'] as FirewallFarbe[]).map((farbe) => (
                      <MenuItem key={farbe} value={farbe}>
                        <Box component="span" sx={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', bgcolor: FARBE_FILL[farbe], mr: 1 }} />
                        {FARBE_LABEL[farbe]}
                      </MenuItem>
                    ))}
                    <MenuItem value="offen">Offen</MenuItem>
                  </Select>
                </FormControl>
                {team.length > 0 && (
                  <FormControl size="small" sx={{ minWidth: 190 }}>
                    <InputLabel>Offen für</InputLabel>
                    <Select
                      label="Offen für"
                      value={String(filters.offenFuer)}
                      onChange={(event) => setFilters((current) => ({
                        ...current,
                        offenFuer: event.target.value === 'alle' ? 'alle' : Number(event.target.value),
                      }))}
                    >
                      <MenuItem value="alle">–</MenuItem>
                      {team.map((member) => <MenuItem key={member.id} value={String(member.id)}>{member.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                )}
                <FormControlLabel
                  control={<Switch size="small" checked={filters.risiko} onChange={(event) => setFilters((current) => ({ ...current, risiko: event.target.checked }))} />}
                  label={<Typography variant="body2">Risikoport</Typography>}
                />
                <FormControlLabel
                  control={(
                    <Switch
                      size="small"
                      color="error"
                      checked={filters.any}
                      inputProps={{ 'data-testid': 'firewall-filter-any' } as React.InputHTMLAttributes<HTMLInputElement>}
                      onChange={(event) => setFilters((current) => ({ ...current, any: event.target.checked }))}
                    />
                  )}
                  label={<Typography variant="body2">Any-Regeln</Typography>}
                />
                <FormControlLabel
                  control={<Switch size="small" checked={filters.abweichung} onChange={(event) => setFilters((current) => ({ ...current, abweichung: event.target.checked }))} />}
                  label={<Typography variant="body2">Abweichung</Typography>}
                />
                {filtersActive && (
                  <Button size="small" onClick={() => setFilters(DEFAULT_RULE_FILTERS)}>Zurücksetzen</Button>
                )}
              </Paper>
            )}

            {geraete.map(renderGeraetBlock)}
          </>
        )}
      </AccordionDetails>

      {/* Firewall-Menü */}
      <Menu anchorEl={geraetMenu?.anchor ?? null} open={Boolean(geraetMenu)} onClose={() => setGeraetMenu(null)}>
        <MenuItem
          data-testid="firewall-geraet-rename"
          onClick={() => { if (geraetMenu) setRenaming({ id: geraetMenu.geraet.id, name: geraetMenu.geraet.name }); setGeraetMenu(null); }}
        >
          <EditOutlinedIcon fontSize="small" sx={{ mr: 1 }} /> Umbenennen
        </MenuItem>
        <MenuItem
          data-testid="firewall-geraet-delete"
          onClick={() => { if (geraetMenu) void openDeleteGeraet(geraetMenu.geraet); setGeraetMenu(null); }}
          sx={{ color: 'error.main' }}
        >
          <DeleteOutlineIcon fontSize="small" sx={{ mr: 1 }} /> Firewall löschen
        </MenuItem>
      </Menu>

      {/* VDOM-Menü */}
      <Menu anchorEl={vdomMenu?.anchor ?? null} open={Boolean(vdomMenu)} onClose={() => setVdomMenu(null)}>
        {canReview && (
          <MenuItem
            onClick={() => { if (vdomMenu) navigate(reviewLink(auditId, 'swipe', vdomMenu.geraet.id, vdomMenu.block.vdom, undefined, filters.farbe)); setVdomMenu(null); }}
          >
            In der Bewertung öffnen
          </MenuItem>
        )}
        {canReview && (
          <MenuItem
            onClick={() => { if (vdomMenu) navigate(reviewLink(auditId, 'final', vdomMenu.geraet.id, vdomMenu.block.vdom, undefined, filters.farbe)); setVdomMenu(null); }}
          >
            Im Finalen Check öffnen
          </MenuItem>
        )}
        <MenuItem
          data-testid="firewall-vdom-move"
          onClick={() => { if (vdomMenu) openMove(vdomMenu.geraet, vdomMenu.block); setVdomMenu(null); }}
        >
          <DriveFileMoveOutlinedIcon fontSize="small" sx={{ mr: 1 }} /> Zu anderer Firewall verschieben
        </MenuItem>
        <MenuItem
          data-testid="firewall-vdom-delete-files"
          aria-label={VDOM_DELETE_FILES_LABEL}
          disabled={!vdomMenu || uploadsForVdom(overview, vdomMenu.geraet, vdomMenu.block).length === 0}
          onClick={() => {
            if (vdomMenu) setUploadPickTarget({ geraet: vdomMenu.geraet, block: vdomMenu.block });
            setVdomMenu(null);
          }}
        >
          <DeleteOutlineIcon fontSize="small" sx={{ mr: 1 }} /> {VDOM_DELETE_FILES_LABEL}
        </MenuItem>
      </Menu>

      <Dialog
        open={uploadPickTarget != null}
        onClose={() => setUploadPickTarget(null)}
        fullWidth
        maxWidth="sm"
        data-testid="firewall-vdom-delete-files-dialog"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>{VDOM_DELETE_FILES_LABEL}</DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          {uploadPickTarget && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload für <strong>{uploadPickTarget.block.label}</strong> auswählen. Die Löschung bestätigen Sie im nächsten Schritt.
            </Typography>
          )}
          {uploadPickTarget && uploadsForVdom(overview, uploadPickTarget.geraet, uploadPickTarget.block).map((upload) => (
            <Button
              key={upload.id}
              fullWidth
              variant="outlined"
              color="inherit"
              data-testid="firewall-vdom-delete-file-option"
              sx={{ justifyContent: 'flex-start', mb: 1, textAlign: 'left' }}
              onClick={() => {
                setDeleteTarget(upload);
                setUploadPickTarget(null);
              }}
            >
              {uploadTitle(upload)}
            </Button>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadPickTarget(null)}>Abbrechen</Button>
        </DialogActions>
      </Dialog>

      {/* VDOM verschieben */}
      <Dialog open={moveTarget != null} onClose={() => { if (!moveBusy) setMoveTarget(null); }} fullWidth maxWidth="xs" data-testid="firewall-move-dialog">
        <DialogTitle sx={{ fontWeight: 700 }}>
          {moveTarget ? `${moveTarget.block.label} verschieben` : 'VDOM verschieben'}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          {moveTarget && (
            <Typography variant="body2" color="text.secondary">
              Von <strong>{moveTarget.geraet.name}</strong>: alle Uploads, Regeln, Adressen und Interfaces dieses VDOMs wandern mit. Bewertungen und Konsens bleiben erhalten.
            </Typography>
          )}
          <FormControl size="small" fullWidth>
            <InputLabel id="fw-move-ziel">Ziel-Firewall</InputLabel>
            <Select
              labelId="fw-move-ziel"
              label="Ziel-Firewall"
              value={moveZiel === '' ? '' : String(moveZiel)}
              inputProps={{ 'data-testid': 'firewall-move-target' }}
              onChange={(event) => { setMoveZiel(Number(event.target.value)); setMoveError(null); }}
            >
              {moveOthers.map((entry) => (
                <MenuItem key={entry.id} value={String(entry.id)}>{entry.name}</MenuItem>
              ))}
              <MenuItem value={String(NEW_IN_MOVE)}>Neue Firewall …</MenuItem>
            </Select>
          </FormControl>
          {moveZiel === NEW_IN_MOVE && (
            <TextField
              size="small"
              fullWidth
              label="Name der neuen Firewall"
              value={moveNeuName}
              inputProps={{ 'data-testid': 'firewall-move-new-name', maxLength: 200 }}
              onChange={(event) => { setMoveNeuName(event.target.value); setMoveError(null); }}
            />
          )}
          {moveConflict && moveTarget && (
            <Alert severity="warning">Die Ziel-Firewall hat schon einen VDOM namens {moveTarget.block.label}.</Alert>
          )}
          {moveError && <Alert severity="error">{moveError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMoveTarget(null)} disabled={moveBusy}>Abbrechen</Button>
          <Button
            variant="contained"
            data-testid="firewall-move-confirm"
            disabled={moveBusy || moveZiel === '' || moveConflict}
            onClick={() => { void confirmMove(); }}
          >
            {moveBusy ? 'Wird verschoben…' : 'Verschieben'}
          </Button>
        </DialogActions>
      </Dialog>

      <Drawer
        anchor="right"
        open={drawerRule != null}
        onClose={() => setDrawerRuleId(null)}
        PaperProps={{ sx: { width: { xs: '100%', md: 760 }, maxWidth: '100%', p: 2, bgcolor: 'background.default' } }}
        data-testid="firewall-rule-drawer"
      >
        {drawerRule && drawerContext && overview && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>Regel {drawerRule.policy_id}</Typography>
              {canReview && (
                <>
                  <Button
                    size="small"
                    variant="contained"
                    data-testid="firewall-drawer-swipe"
                    onClick={() => navigate(reviewLink(auditId, 'swipe', drawerContext.geraet.id, drawerContext.block.vdom, drawerRule.id, filters.farbe))}
                  >
                    In der Bewertung öffnen
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    color="secondary"
                    data-testid="firewall-drawer-final"
                    onClick={() => navigate(reviewLink(auditId, 'final', drawerContext.geraet.id, drawerContext.block.vdom, drawerRule.id, filters.farbe))}
                  >
                    Im Finalen Check öffnen
                  </Button>
                </>
              )}
              <IconButton aria-label="schließen" onClick={() => setDrawerRuleId(null)}>
                <CloseIcon />
              </IconButton>
            </Box>
            <FirewallRuleCard
              rule={drawerRule}
              vdomLabel={drawerContext.block.label}
              firewallName={geraete.length > 1 ? drawerContext.geraet.name : undefined}
              rules={drawerContext.block.regeln}
              statistik={overview.statistik}
              vdomStatistik={drawerContext.block.statistik}
              team={team}
              showPeerRatings
              showPeerTexts={false}
              compact
              onJumpToRule={(ruleId) => setDrawerRuleId(ruleId)}
            />
          </Box>
        )}
      </Drawer>

      <ConfirmDialog
        open={deleteTarget != null}
        title={deleteTarget ? `${TYP_LABELS[deleteTarget.typ]}-Upload löschen` : 'Upload löschen'}
        message={deleteTarget ? `${deleteTarget.dateiname}${deleteTarget.vdom ? ` (VDOM ${deleteTarget.vdom})` : ''}` : ''}
        additionalContent={deleteTarget ? (
          <Alert
            severity={deleteTarget.typ === 'policies' ? 'error' : 'warning'}
            sx={{ mt: 1.5 }}
            data-testid="firewall-delete-counts"
          >
            {deleteMessage}
          </Alert>
        ) : null}
        confirmText={deleting ? 'Wird gelöscht…' : 'Löschen'}
        onConfirm={() => { if (!deleting) void confirmDelete(); }}
        onCancel={() => setDeleteTarget(null)}
      />

      <ConfirmDialog
        open={deleteGeraet != null}
        title={deleteGeraet ? `Firewall „${deleteGeraet.geraet.name}“ löschen` : 'Firewall löschen'}
        message={deleteGeraet ? `${plural(deleteGeraet.geraet.vdoms.length, 'VDOM', 'VDOMs')} mit allen Uploads und Regeln.` : ''}
        additionalContent={deleteGeraet ? (
          <Alert severity="error" sx={{ mt: 1.5 }} data-testid="firewall-geraet-delete-counts">
            {geraetDeleteMessage}
          </Alert>
        ) : null}
        confirmText={deleting ? 'Wird gelöscht…' : 'Löschen'}
        onConfirm={() => { if (!deleting && deleteGeraet?.counts) void confirmDeleteGeraet(); }}
        onCancel={() => setDeleteGeraet(null)}
      />
    </Accordion>
  );
};

export default React.memo(AuditFirewallSection);
